import { ActionContext } from 'vuex';

import { getLocalState } from '@/common/utils/store';

import {
  TUserGroupListsModalSaveAreaParams,
  TUserGroupListsModalSaveGroupItemsParamsCustom,
  TUserGroupListsModalSaveGroupItemsParamsTransport,
  TUserGroupListsModalTransportSaveData,
  TUserGroupListsModalTreeListUpdateGroupCustom,
} from '../../types/user-group-lists-modal';
import { USER_GROUPS_ACTIONS_NAME, USER_GROUPS_MUTATIONS_NAME } from './name';
import { TUserGroupsState } from './types';

import links from '@/utils/links';
import request from '@/utils/request';

export async function searchCountries(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.areas.getCountries,
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setCountries),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchCountries),
    },
    {},
  );
}

export async function searchAreas(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.areas.getAreas,
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setAreas),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchAreas),
    },
    {},
  );
}

export async function searchAreaLinks(
  context: ActionContext<TUserGroupsState, unknown>,
  payload: { areaId: string; abortController?: AbortController },
) {
  return request(
    context,
    {
      url: links.ved.userGroups.areas.getAreaLinks,
      pathParams: ['areaId'],
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setAreaCountryLinks),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchAreaLinks),
      abortController: payload.abortController,
    },
    { areaId: payload.areaId },
  );
}

export async function createArea(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalSaveAreaParams,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.areas.createArea,
      method: 'post',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.createArea),
    },
    params,
  );
}

export async function updateArea(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalSaveAreaParams,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.areas.updateArea,
      method: 'put',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.updateArea),
    },
    params,
  );
}

export async function deleteArea(context: ActionContext<TUserGroupsState, unknown>, areaId: string) {
  return request(
    context,
    {
      url: links.ved.userGroups.areas.deleteArea,
      method: 'delete',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.deleteArea),
    },
    { id: areaId },
  );
}

export async function saveAreaCountries(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalSaveGroupItemsParamsCustom,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.areas.saveAreaCountries,
      method: 'put',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.saveAreaCountries),
    },
    {
      id: params.groupId,
      countryIds: params.itemIds,
    },
  );
}

export async function searchCustomsItems(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.customs.getItems,
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setCustomsItems),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchCustomsItems),
    },
    {},
  );
}

export async function searchCustomsLinks(
  context: ActionContext<TUserGroupsState, unknown>,
  payload: { groupId: number; abortController?: AbortController },
) {
  return request(
    context,
    {
      url: links.ved.userGroups.customs.getLinks,
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchCustomsLinks),
      abortController: payload.abortController,
    },
    { id: payload.groupId },
  );
}

export async function createCustomsGroup(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalTreeListUpdateGroupCustom,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.customs.createGroup,
      method: 'post',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.createCustomsGroup),
    },
    {
      kod: params.kod,
      name: params.name,
      parent: params.parent,
    },
  );
}

export async function updateCustomsGroup(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalTreeListUpdateGroupCustom,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.customs.updateGroup,
      method: 'put',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.updateCustomsGroup),
    },
    {
      id: params.id,
      kod: params.kod,
      name: params.name,
      parent: params.parent,
    },
  );
}

export async function deleteCustomsGroup(context: ActionContext<TUserGroupsState, unknown>, groupId: number) {
  return request(
    context,
    {
      url: links.ved.userGroups.customs.deleteGroup,
      method: 'delete',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.deleteCustomsGroup),
    },
    { id: groupId },
  );
}

export async function saveCustomsGroupItems(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalSaveGroupItemsParamsCustom,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.customs.saveGroupItems,
      method: 'put',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.saveCustomsGroupItems),
    },
    { id: params.groupId, itemIds: params.itemIds },
  );
}

export async function searchTransportGroups(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.transport.getGroups,
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setTransportGroups),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchTransportGroups),
    },
    {},
  );
}

export async function searchTransportItems(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.transport.getItems,
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setTransportItems),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchTransportItems),
    },
    {},
  );
}

export async function searchTransportLinks(
  context: ActionContext<TUserGroupsState, unknown>,
  payload: { groupId: number; abortController?: AbortController },
) {
  return request(
    context,
    {
      url: links.ved.userGroups.transport.getLinks,
      method: 'get',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchTransportLinks),
      abortController: payload.abortController,
    },
    { id: payload.groupId },
  );
}

export async function createTransportGroup(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalTransportSaveData,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.transport.createGroup,
      method: 'post',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.createTransportGroup),
    },
    {
      name: params.name,
      code: params.code,
      parent: params.parent,
    },
  );
}

export async function updateTransportGroup(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalTransportSaveData,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.transport.updateGroup,
      method: 'put',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.updateTransportGroup),
    },
    {
      id: params.id,
      name: params.name,
      code: params.code,
      parent: params.parent,
    },
  );
}

export async function deleteTransportGroup(context: ActionContext<TUserGroupsState, unknown>, groupId: number) {
  return request(
    context,
    {
      url: links.ved.userGroups.transport.deleteGroup,
      method: 'delete',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.deleteTransportGroup),
    },
    { id: groupId },
  );
}

export async function saveTransportGroupItems(
  context: ActionContext<TUserGroupsState, unknown>,
  params: TUserGroupListsModalSaveGroupItemsParamsTransport,
) {
  return request(
    context,
    {
      url: links.ved.userGroups.transport.updateRelations,
      method: 'put',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.saveTransportGroupItems),
    },
    params,
  );
}

export async function searchGoodsItems(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.getItems,
      method: 'get',
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setGoodsItems),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchGoodsItems),
    },
    {
      sort: 1,
    },
  );
}

export async function searchGoodsGroups(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.getGroups,
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setGoodsGroups),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchGoodsGroups),
    },
    {},
  );
}

export async function fetchGoodsGroupById(context: ActionContext<TUserGroupsState, unknown>, groupId: number) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.getGroup,
      method: 'get',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.fetchGoodsGroupById),
    },
    { id: groupId },
  );
}

export async function searchGoodsLinks(
  context: ActionContext<TUserGroupsState, unknown>,
  payload: { groupId: number; abortController?: AbortController },
) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.getLinks,
      method: 'get',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchGoodsLinks),
      abortController: payload.abortController,
    },
    { id: payload.groupId },
  );
}

export async function createGoodsGroup(
  context: ActionContext<TUserGroupsState, unknown>,
  params: { name: string; code: string; parent: number; izm?: string },
) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.createGroup,
      method: 'post',
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.createGoodsGroup),
    },
    params,
  );
}

export async function updateGoodsGroup(
  context: ActionContext<TUserGroupsState, unknown>,
  params: { id: number; name: string; code: string; parent: number; izm?: string },
) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.updateGroup,
      method: 'put',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.updateGoodsGroup),
    },
    { id: params.id, name: params.name, code: params.code, parent: params.parent, izm: params.izm },
  );
}

export async function deleteGoodsGroup(context: ActionContext<TUserGroupsState, unknown>, groupId: number) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.deleteGroup,
      method: 'delete',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.deleteGoodsGroup),
    },
    { id: groupId },
  );
}

export async function saveGoodsGroupGoods(
  context: ActionContext<TUserGroupsState, unknown>,
  payload: { groupId: number; goodsIds: string[] },
) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.saveConnections,
      method: 'put',
      pathParams: ['id'],
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.saveGoodsGroupGoods),
    },
    { id: payload.groupId, tnvedIds: payload.goodsIds },
  );
}

export async function searchGoodsUnits(context: ActionContext<TUserGroupsState, unknown>) {
  return request(
    context,
    {
      url: links.ved.userGroups.goods.getFtsEdizmMeasurementUnits,
      method: 'get',
      mutate: getLocalState(USER_GROUPS_MUTATIONS_NAME.setGoodsUnits),
      name: getLocalState(USER_GROUPS_ACTIONS_NAME.searchGoodsUnits),
    },
    {},
  );
}
